export interface Me {
  id: string;
  display: string;
  isAdmin: boolean;
}

export interface User {
  id: string;
  entra_oid: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  created_utc: string;
  last_login_utc: string | null;
}

export interface BucketInfo {
  logicalName: string;
  bindingName?: string;
  isPublic: boolean;
  customDomain?: string | null;
  description?: string | null;
}

export interface ObjectItem {
  key: string;
  size: number;
  etag?: string | null;
  uploaded: string;
  contentType?: string | null;
}

export interface ObjectsPage {
  items: ObjectItem[];
  prefixes: string[];
  nextCursor: string | null;
}

export type PrincipalType = 'apikey' | 'oauth';

export interface AclEntry {
  id: string;
  name: string;
  principal_type: PrincipalType;
  key_prefix?: string | null;
  oauth_subject?: string | null;
  oauth_email?: string | null;
  oauth_tenant?: string | null;
  oauth_app_id?: string | null;
  oauth_audience?: string | null;
  oauth_issuer?: string | null;
  created_utc: string;
  expires_utc?: string | null;
  last_used_utc?: string | null;
  revoked: number;
  /** The entry's grants, included by the list endpoint for filtering and details. */
  grants?: Grant[];
}

export interface Grant {
  id: string;
  bucket: string;
  path_prefix: string;
  perms: number;
}

/** A public (anonymous) access grant: (bucket, path_prefix) readable by anyone. */
export interface PublicGrant {
  id: string;
  bucket: string;
  path_prefix: string;
  perms: number;
  created_utc: string;
}

export interface CreateAclResult {
  id: string;
  name: string;
  principalType: PrincipalType;
  token?: string;
  keyPrefix?: string;
}

export interface StatsOverview {
  buckets: { logicalName: string; isPublic: boolean; description?: string | null }[];
  recent: { bucket: string; op: string; count: number; bytes: number }[];
}

/** Estimated month-to-date Cloudflare spend (see worker lib/costs.ts). */
export interface CostsResponse {
  configured: boolean;
  from?: string;
  to?: string;
  generatedUtc?: string;
  cached?: boolean;
  planBaseUsd?: number;
  lines?: {
    product: string;
    item: string;
    usage: string;
    included: string;
    costUsd: number;
    azureUsd: number | null;
    awsUsd: number | null;
  }[];
  mtdUsd?: number;
  projectedUsd?: number;
  azureMtdUsd?: number;
  awsMtdUsd?: number;
  warnings?: string[];
}

export interface AccessSeries {
  groupBy: string;
  from: string;
  to: string;
  series: { bucket: string; count: number; bytes: number; egress_bytes: number; ingress_bytes: number }[];
}

/** One principal that can reach a given object/folder, via a grant. */
export interface AccessPrincipal {
  entryId: string;
  name: string;
  principalType: 'apikey' | 'oauth';
  principal: string;
  pathPrefix: string;
  perms: number;
}

/** Who can access a given object (key) or folder (prefix). */
export interface AccessInfo {
  bucket: string;
  key: string | null;
  prefix: string | null;
  acl: AccessPrincipal[];
  public: { id: string; pathPrefix: string; perms: number }[];
}

/** A single raw access event (per-access traceability). */
export interface AccessEventRow {
  ts_utc: string;
  actor_type: string;
  actor_id: string;
  op: string;
  bytes: number | null;
  status: number;
  ip: string | null;
}

/** Per-blob lifetime usage: read/write counts, ingress/egress, recent events. */
export interface BlobStats {
  bucket: string;
  key: string;
  stats: {
    reads: number;
    writes: number;
    egress_bytes: number;
    ingress_bytes: number;
    last_access_utc: string | null;
  };
  recent: AccessEventRow[];
}

/** One node of the bucket size tree (folder, file, or '(other)' rollup). */
export interface SizeNode {
  name: string;
  /** Full prefix ending '/' for folders; full key for files; '' for the root. */
  path: string;
  size: number;
  count: number;
  kind: 'folder' | 'file' | 'other';
  children?: SizeNode[];
}

/** Whole-bucket size tree for the dashboard treemap. */
export interface SizemapResponse {
  bucket: string;
  generatedUtc: string;
  truncated: boolean;
  cached: boolean;
  totalSize: number;
  totalCount: number;
  root: SizeNode;
}

/** An automation type available in the Worker's registry. */
export interface AutomationTypeInfo {
  name: string;
  description: string;
}

/** Config of the 'retention-cleanup' automation type, mirroring the Worker. */
export interface RetentionConfig {
  bucket: string;
  pattern: string;
  olderThanDays?: number;
  keepNewest?: number;
  maxDeletesPerRun?: number;
}

export type AutomationStatus = 'ok' | 'warning' | 'error';

/** An admin-defined automation (config shape depends on `type`). */
export interface Automation {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  config: RetentionConfig | Record<string, unknown>;
  interval_minutes: number;
  created_utc: string;
  updated_utc: string;
  last_run_utc: string | null;
  last_status: AutomationStatus | null;
  last_error: string | null;
}

/** One recorded execution of an automation (real or dry-run). */
export interface AutomationRun {
  id: string;
  automation_id: string;
  started_utc: string;
  finished_utc: string | null;
  trigger: 'cron' | 'manual';
  dry_run: boolean;
  status: 'running' | AutomationStatus;
  scanned: number;
  matched: number;
  affected: number;
  sampleKeys: string[];
  error: string | null;
}

/** An MCP agent's sign-in, waiting on the consent page for the user to allow or cancel it. */
export interface McpAuthRequest {
  /** As the agent describes itself at registration; the redirect host is what can be trusted. */
  clientName: string | null;
  redirectUri: string;
  redirectHost: string;
  /** The agent listens on this computer (a localhost redirect), like Claude Code or Cursor. */
  loopback: boolean;
  expiresUtc: string;
  sessionDays: number;
  user: { email: string; isAdmin: boolean };
}

/** One of the signed-in user's connected MCP agents. */
export interface McpConnection {
  id: string;
  clientName: string | null;
  redirectHost: string;
  loopback: boolean;
  createdUtc: string;
  lastUsedUtc: string | null;
  expiresUtc: string;
}

/** Permission bitflags, mirroring the Worker. */
export const Perm = { READ: 1, WRITE: 2, LIST: 4, DELETE: 8 } as const;
